import { WeatherData, LocationData, WeatherError, WeatherErrorType, WeatherCondition } from './types';
import { WeatherConfigManager, WeatherAPIEndpoint, LocationAPIEndpoint } from './config';
import { PerformanceMonitor } from './utils/performanceMonitor';

export interface APIResponse<T> {
  data: T;
  source: string;
  timestamp: Date;
  fromCache: boolean;
}

export class WeatherAPIManager {
  private static readonly OPENWEATHER_BASE_URL = 'https://api.openweathermap.org/data/2.5/weather';
  private static readonly WEATHERAPI_BASE_URL = 'https://api.weatherapi.com/v1/current.json';

  static async fetchWeatherData(location: LocationData): Promise<WeatherData> {
    const config = WeatherConfigManager.getConfig();
    const availableAPI = WeatherConfigManager.getNextAvailableWeatherAPI();

    if (!availableAPI) {
      throw new WeatherError(
        WeatherErrorType.WEATHER_API_FAILED,
        'No weather APIs available'
      );
    }

    if (config.debugMode) {
      console.log(`🌤️ Using weather API: ${availableAPI.name} (priority: ${availableAPI.priority})`);
    }

    try {
      const weatherData = await this.callWeatherAPI(availableAPI, location);
      WeatherConfigManager.trackAPIUsage(availableAPI.name);
      return weatherData;
    } catch (error) {
      if (config.debugMode) {
        console.warn(`🌤️ Weather API ${availableAPI.name} failed:`, error);
      }

      // Try fallback APIs if rotation is enabled
      if (config.enableAPIRotation && config.enableFallbackServices) {
        return this.tryFallbackWeatherAPIs(availableAPI, location, error as Error);
      }

      throw error;
    }
  }

  private static async tryFallbackWeatherAPIs(
    failedAPI: WeatherAPIEndpoint,
    location: LocationData,
    originalError: Error
  ): Promise<WeatherData> {
    const config = WeatherConfigManager.getConfig();
    const allAPIs = WeatherConfigManager.getActiveWeatherAPIs();
    const fallbackAPIs = allAPIs.filter(api => api.name !== failedAPI.name);

    if (config.debugMode) {
      console.log(`🌤️ Trying ${fallbackAPIs.length} fallback weather APIs`);
    }

    for (const api of fallbackAPIs) {
      try {
        const weatherData = await this.callWeatherAPI(api, location);
        WeatherConfigManager.trackAPIUsage(api.name);
        
        if (config.debugMode) {
          console.log(`🌤️ Fallback API ${api.name} succeeded`);
        }
        
        return weatherData;
      } catch (fallbackError) {
        if (config.debugMode) {
          console.warn(`🌤️ Fallback API ${api.name} also failed:`, fallbackError);
        }
        continue;
      }
    }

    // All APIs failed, throw the original error
    throw originalError;
  }

  private static async callWeatherAPI(
    api: WeatherAPIEndpoint,
    location: LocationData
  ): Promise<WeatherData> {
    const startTime = Date.now();

    try {
      let weatherData: WeatherData;

      switch (api.name) {
        case 'openweathermap':
        case 'openweathermap-2':
          weatherData = await this.callOpenWeatherMapAPI(api, location);
          break;
        case 'weatherapi-com':
          weatherData = await this.callWeatherAPIComAPI(api, location);
          break;
        default:
          throw new WeatherError(
            WeatherErrorType.WEATHER_API_FAILED,
            `Unknown weather API: ${api.name}`
          );
      }

      const duration = Date.now() - startTime;
      PerformanceMonitor.recordMetric('weather_api_response_time', duration, {
        api: api.name,
        success: true,
      });

      return weatherData;
    } catch (error) {
      const duration = Date.now() - startTime;
      PerformanceMonitor.recordMetric('weather_api_response_time', duration, {
        api: api.name,
        success: false,
      });

      throw error;
    }
  }

  private static async callOpenWeatherMapAPI(
    api: WeatherAPIEndpoint,
    location: LocationData
  ): Promise<WeatherData> {
    const url = new URL(this.OPENWEATHER_BASE_URL);
    url.searchParams.set('lat', location.coordinates.latitude.toString());
    url.searchParams.set('lon', location.coordinates.longitude.toString());
    url.searchParams.set('appid', api.apiKey);
    url.searchParams.set('units', 'metric');
    url.searchParams.set('lang', 'es');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), api.timeout);

    try {
      const response = await fetch(url.toString(), {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 429) {
          throw new WeatherError(
            WeatherErrorType.API_LIMIT_EXCEEDED,
            `OpenWeatherMap API rate limit exceeded for ${api.name}`
          );
        }

        throw new WeatherError(
          WeatherErrorType.WEATHER_API_FAILED,
          `OpenWeatherMap API returned ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();

      return {
        temperature: Math.round(data.main.temp),
        condition: this.mapOpenWeatherCondition(data.weather[0].main, data.weather[0].id),
        description: data.weather[0].description,
        humidity: data.main.humidity,
        windSpeed: data.wind?.speed || 0,
        location: {
          city: data.name || location.city,
          country: data.sys?.country || location.country,
          coordinates: {
            lat: data.coord.lat,
            lon: data.coord.lon,
          },
        },
        timestamp: new Date(),
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof WeatherError) {
        throw error;
      }

      throw new WeatherError(
        WeatherErrorType.NETWORK_ERROR,
        `Failed to fetch from OpenWeatherMap (${api.name})`,
        error as Error
      );
    }
  }

  private static async callWeatherAPIComAPI(
    api: WeatherAPIEndpoint,
    location: LocationData
  ): Promise<WeatherData> {
    const url = new URL(this.WEATHERAPI_BASE_URL);
    url.searchParams.set('key', api.apiKey);
    url.searchParams.set('q', `${location.coordinates.latitude},${location.coordinates.longitude}`);
    url.searchParams.set('lang', 'es');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), api.timeout);

    try {
      const response = await fetch(url.toString(), {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 429) {
          throw new WeatherError(
            WeatherErrorType.API_LIMIT_EXCEEDED,
            `WeatherAPI.com rate limit exceeded`
          );
        }

        throw new WeatherError(
          WeatherErrorType.WEATHER_API_FAILED,
          `WeatherAPI.com returned ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();

      return {
        temperature: Math.round(data.current.temp_c),
        condition: this.mapWeatherAPIComCondition(data.current.condition.code),
        description: data.current.condition.text,
        humidity: data.current.humidity,
        windSpeed: data.current.wind_kph / 3.6, // Convert km/h to m/s
        location: {
          city: data.location.name || location.city,
          country: data.location.country || location.country,
          coordinates: {
            lat: data.location.lat,
            lon: data.location.lon,
          },
        },
        timestamp: new Date(),
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof WeatherError) {
        throw error;
      }

      throw new WeatherError(
        WeatherErrorType.NETWORK_ERROR,
        'Failed to fetch from WeatherAPI.com',
        error as Error
      );
    }
  }

  private static mapOpenWeatherCondition(main: string, id: number): WeatherCondition {
    const hour = new Date().getHours();
    const isNight = hour < 6 || hour > 20;

    switch (main.toLowerCase()) {
      case 'clear':
        return isNight ? 'clear-night' : 'clear-day';
      case 'clouds':
        return id <= 802 ? 'partly-cloudy' : 'cloudy';
      case 'rain':
      case 'drizzle':
        return 'rain';
      case 'snow':
        return 'snow';
      case 'mist':
      case 'fog':
      case 'haze':
        return 'fog';
      case 'thunderstorm':
        return 'rain';
      default:
        return 'default';
    }
  }

  private static mapWeatherAPIComCondition(code: number): WeatherCondition {
    const hour = new Date().getHours();
    const isNight = hour < 6 || hour > 20;

    // WeatherAPI.com condition codes mapping
    if (code === 1000) { // Sunny/Clear
      return isNight ? 'clear-night' : 'clear-day';
    } else if ([1003, 1006].includes(code)) { // Partly cloudy, Cloudy
      return code === 1003 ? 'partly-cloudy' : 'cloudy';
    } else if ([1009, 1030, 1135, 1147].includes(code)) { // Overcast, Mist, Fog
      return 'fog';
    } else if ([1063, 1150, 1153, 1168, 1171, 1180, 1183, 1186, 1189, 1192, 1195, 1198, 1201, 1240, 1243, 1246].includes(code)) { // Rain variations
      return 'rain';
    } else if ([1066, 1069, 1072, 1114, 1117, 1204, 1207, 1210, 1213, 1216, 1219, 1222, 1225, 1237, 1249, 1252, 1255, 1258, 1261, 1264].includes(code)) { // Snow variations
      return 'snow';
    } else if ([1087, 1273, 1276, 1279, 1282].includes(code)) { // Thunderstorm variations
      return 'rain';
    }

    return 'default';
  }
}

export class LocationAPIManager {
  static async fetchLocationData(): Promise<LocationData> {
    const config = WeatherConfigManager.getConfig();
    const availableAPI = WeatherConfigManager.getNextAvailableLocationAPI();

    if (!availableAPI) {
      throw new WeatherError(
        WeatherErrorType.LOCATION_DETECTION_FAILED,
        'No location APIs available'
      );
    }

    if (config.debugMode) {
      console.log(`📍 Using location API: ${availableAPI.name} (priority: ${availableAPI.priority})`);
    }

    try {
      return await this.callLocationAPI(availableAPI);
    } catch (error) {
      if (config.debugMode) {
        console.warn(`📍 Location API ${availableAPI.name} failed:`, error);
      }

      // Try fallback APIs if rotation is enabled
      if (config.enableAPIRotation && config.enableFallbackServices) {
        return this.tryFallbackLocationAPIs(availableAPI, error as Error);
      }

      throw error;
    }
  }

  private static async tryFallbackLocationAPIs(
    failedAPI: LocationAPIEndpoint,
    originalError: Error
  ): Promise<LocationData> {
    const config = WeatherConfigManager.getConfig();
    const allAPIs = WeatherConfigManager.getActiveLocationAPIs();
    const fallbackAPIs = allAPIs.filter(api => api.name !== failedAPI.name);

    if (config.debugMode) {
      console.log(`📍 Trying ${fallbackAPIs.length} fallback location APIs`);
    }

    for (const api of fallbackAPIs) {
      try {
        const locationData = await this.callLocationAPI(api);
        
        if (config.debugMode) {
          console.log(`📍 Fallback API ${api.name} succeeded`);
        }
        
        return locationData;
      } catch (fallbackError) {
        if (config.debugMode) {
          console.warn(`📍 Fallback API ${api.name} also failed:`, fallbackError);
        }
        continue;
      }
    }

    // All APIs failed, throw the original error
    throw originalError;
  }

  private static async callLocationAPI(api: LocationAPIEndpoint): Promise<LocationData> {
    const startTime = Date.now();

    try {
      let locationData: LocationData;

      switch (api.name) {
        case 'ipapi-co':
          locationData = await this.callIPAPICoAPI(api);
          break;
        case 'ip-api-com':
          locationData = await this.callIPAPIComAPI(api);
          break;
        case 'ipgeolocation-io':
          locationData = await this.callIPGeolocationAPI(api);
          break;
        default:
          throw new WeatherError(
            WeatherErrorType.LOCATION_DETECTION_FAILED,
            `Unknown location API: ${api.name}`
          );
      }

      const duration = Date.now() - startTime;
      PerformanceMonitor.recordMetric('location_api_response_time', duration, {
        api: api.name,
        success: true,
      });

      return locationData;
    } catch (error) {
      const duration = Date.now() - startTime;
      PerformanceMonitor.recordMetric('location_api_response_time', duration, {
        api: api.name,
        success: false,
      });

      throw error;
    }
  }

  private static async callIPAPICoAPI(api: LocationAPIEndpoint): Promise<LocationData> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), api.timeout);

    try {
      const response = await fetch(api.baseUrl, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new WeatherError(
          WeatherErrorType.LOCATION_DETECTION_FAILED,
          `ipapi.co returned ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();

      if (data.error) {
        throw new WeatherError(
          WeatherErrorType.LOCATION_DETECTION_FAILED,
          `ipapi.co error: ${data.reason}`
        );
      }

      return {
        ip: data.ip,
        city: data.city,
        region: data.region,
        country: data.country_name,
        coordinates: {
          latitude: data.latitude,
          longitude: data.longitude,
        },
        timezone: data.timezone,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof WeatherError) {
        throw error;
      }

      throw new WeatherError(
        WeatherErrorType.NETWORK_ERROR,
        'Failed to fetch from ipapi.co',
        error as Error
      );
    }
  }

  private static async callIPAPIComAPI(api: LocationAPIEndpoint): Promise<LocationData> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), api.timeout);

    try {
      const response = await fetch(api.baseUrl, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new WeatherError(
          WeatherErrorType.LOCATION_DETECTION_FAILED,
          `ip-api.com returned ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();

      if (data.status === 'fail') {
        throw new WeatherError(
          WeatherErrorType.LOCATION_DETECTION_FAILED,
          `ip-api.com error: ${data.message}`
        );
      }

      return {
        ip: data.query,
        city: data.city,
        region: data.regionName,
        country: data.country,
        coordinates: {
          latitude: data.lat,
          longitude: data.lon,
        },
        timezone: data.timezone,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof WeatherError) {
        throw error;
      }

      throw new WeatherError(
        WeatherErrorType.NETWORK_ERROR,
        'Failed to fetch from ip-api.com',
        error as Error
      );
    }
  }

  private static async callIPGeolocationAPI(api: LocationAPIEndpoint): Promise<LocationData> {
    if (!api.apiKey) {
      throw new WeatherError(
        WeatherErrorType.LOCATION_DETECTION_FAILED,
        'IPGeolocation API key not configured'
      );
    }

    const url = new URL(api.baseUrl);
    url.searchParams.set('apiKey', api.apiKey);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), api.timeout);

    try {
      const response = await fetch(url.toString(), {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new WeatherError(
          WeatherErrorType.LOCATION_DETECTION_FAILED,
          `IPGeolocation returned ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();

      if (data.message) {
        throw new WeatherError(
          WeatherErrorType.LOCATION_DETECTION_FAILED,
          `IPGeolocation error: ${data.message}`
        );
      }

      return {
        ip: data.ip,
        city: data.city,
        region: data.state_prov,
        country: data.country_name,
        coordinates: {
          latitude: parseFloat(data.latitude),
          longitude: parseFloat(data.longitude),
        },
        timezone: data.time_zone.name,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof WeatherError) {
        throw error;
      }

      throw new WeatherError(
        WeatherErrorType.NETWORK_ERROR,
        'Failed to fetch from IPGeolocation',
        error as Error
      );
    }
  }
}